import React, { useRef, useCallback, useState, useEffect } from 'react';
import { TemplatesProvider, useTemplatesState, useTemplatesDispatch, NEXT_PAGE, PREVIOUS_PAGE, SELECT, TOGGLE_CREATE, HANDLE_SEARCH, useTemplates, TOGGLE_TEMPLATE_DETAIL, SET_CURRENT_TEMPLATE, SELECT_ALL_CONTRACTS, SET_PAGE } from 'context/templates';
import { bulkDeleteTemplates, createContractFromTemplate, createTemplate, fetchTemplates, fetchTemplatesFavourites, templateAction } from 'api/actions/templates';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { uploadFileV2 } from 'api/actions/file';
import { toast } from 'components/Shared/Toast';
import { useTranslation } from 'react-i18next';
import { fetcher } from 'api/client';
import { parseDate, useCancellablePromises, truncate } from 'utils';
import Dropzone from 'react-dropzone';
import clsx from 'clsx';
import {
    DropdownItem, DropdownMenu, DropdownToggle, CardTitle, Card, CardText, ButtonGroup, UncontrolledDropdown, InputGroup, Breadcrumb, BreadcrumbItem, CardBody, Container, Row, Nav, Navbar, Table, Input, Button, NavbarText, NavItem,
    Modal,
    Col,
    ModalHeader,
    ModalBody,
    CardGroup,
    CustomInput,
    FormGroup,
    Label,
    Spinner,
    CardDeck,
    CardFooter,
} from 'reactstrap';
import bootbox from 'components/Modals/Bootbox';
import { useHistory } from 'react-router-dom';
import useTemplateActions from 'hooks/useTemplateActions';
import TemplateDetail from 'components/ContractDetail/TemplateDetail';
import Pagination from 'components/Shared/Paginator';
import { useProfileAbility } from 'context/profile';
import { MainHeader } from 'components/Shared/MainHeader';

const restricted = ['INPUT', 'BUTTON', 'I', 'LABEL'];

function FavouriteItem({ fav, editTemplate, unfavorite, handleDoubleClick }) {

    const { t } = useTranslation();
    const { _id, name, updatedAt, } = fav;

    const handleEditTemplate = () => {
        editTemplate(_id)
    }
    const handleUnfavorite = () => {
        unfavorite(_id)
    }
    const doubleClick = (e) => {
        if (restricted.includes(e.target.tagName)) return;
        handleDoubleClick(_id)
    }

    return (
        <Card onDoubleClick={doubleClick} className="pointer card-shadow rounded-lg mr-2 mb-2">
            <CardBody style={{ maxWidth: '180px', minWidth: '180px' }}>
                <CardTitle className="m-0 text-truncate">
                    <div onClick={handleUnfavorite} className="float-right" >
                        <i className="fas fa-star text-warning"></i>
                    </div>
                    {name}
                </CardTitle>
                <CardText className="m-0">
                    <small className="text-muted">
                        {t('templates.listing.updated')}{' '}{parseDate(updatedAt, 'PP')}
                    </small>
                </CardText>
                <CardText className="m-0">
                    <small>
                        {t('templates.status.private')}
                    </small>
                </CardText>
                <CardText>
                    <Button onClick={handleEditTemplate} size="sm" color="light" className="float-right">
                        {t('templates.options.edit')}
                    </Button>
                </CardText>
            </CardBody>
        </Card>
    )
}

function Favourites({ handleDoubleClick }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const history = useHistory();
    const { data, isLoading } = useQuery(['template-favourites'], () => fetcher(fetchTemplatesFavourites()));
    const editTemplate = (_id) => {
        history.push(`/templates/editor/${_id}`)
    }

    const { mutate: unfavoriteMutation } = useMutation((payload) => fetcher(templateAction(payload.templateId, 'unfavorite', {})), {
        onSuccess: (response) => {
            queryClient.invalidateQueries(['templates-all'])
            queryClient.invalidateQueries(['template-favourites']);
        }
    });
    const unfavorite = async (_id) => {
        unfavoriteMutation({ templateId: _id })
    }

    return (
        <Container fluid className="mb-2 mt-2 pl-0">
            <h5>
                {t('templates.favourites.title')}
            </h5>
            <Row>
                {isLoading ?
                    <Card className="p-4 shadow-sm  bg-light-gray pulse-animation" style={{ minWidth: '210px', minHeight: '126px' }}>
                        {/* <CardBody ><Spinner size="sm" />{' '}{t('templates.listing.loading')}</CardBody> */}
                    </Card>
                    :
                    data?.length ?
                        data.map((fav) => (
                            <FavouriteItem
                                key={fav._id}
                                fav={fav}
                                editTemplate={editTemplate}
                                unfavorite={unfavorite}
                                handleDoubleClick={handleDoubleClick}
                            />
                        ))
                        :
                        t('templates.listing.empty')
                }
            </Row>
        </Container>
    )
}

function Header() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState({ searchType: 'name', searchValue: '' });
    const templates = useTemplatesState();
    const dispatch = useTemplatesDispatch();

    const { mutate: deleteMutation } = useMutation((payload) => fetcher(bulkDeleteTemplates(payload)), {
        onSuccess: () => {
            queryClient.invalidateQueries(['templates-all'])
            toast.success(t('templates.listing.delete_success'));
        }
    })

    const deleteSelected = async () => {
        if (!templates?.selected?.length) return;
        if (await bootbox.confirm(t('templates.delete.modal')))
            deleteMutation({ tbd: templates.selected })
    }

    const openCreate = useCallback(() => dispatch({ type: TOGGLE_CREATE }), [dispatch])

    useEffect(() => {
        dispatch({ type: HANDLE_SEARCH, search: `${search.searchType}|${search.searchValue}` })
    }, [dispatch, search.searchType, search.searchValue])

    const handleSearch = (e) => {
        setSearch({ ...search, [e.target.name]: e.target.value })
    }
    return (
        <MainHeader title="templates.title">
            <InputGroup inlist="true" className="align-items-center">
                <Input type="select" className="m-0 p-0 px-2" value={search.searchType} name="searchType" onChange={handleSearch}>
                    <option value="name">{t('templates.listing.name')}</option>
                    <option value="content">{t('templates.search.content')}</option>
                </Input>
                <Input type="text" placeholder={t('templates.search.placeholder')} name="searchValue" onChange={handleSearch} />
                <ButtonGroup>
                    <Button color="white" title={t('templates.tooltip.delete')} onClick={deleteSelected}>
                        <i className="fas fa-trash fa-2x"></i>
                    </Button>
                    <Button color="white" title={t('templates.tooltip.new_template')} onClick={openCreate}>
                        <i className="fas fa-plus fa-2x text-primary"></i>
                    </Button>
                </ButtonGroup>
            </InputGroup>
        </MainHeader>
    )
}

function MenuDropdown({ template }) {
    const { t } = useTranslation();
    const [state, dispatch] = useTemplates();
    const availableActions = useTemplateActions(template, {
        listOnly: state.meta.listOnly,
        refetch: state.query,
        // onUpdateContract: (newContract) => {
        //     dispatch({
        //         type: UPDATE_CONTRACT, contract: {
        //             ...contract,
        //             ...newContract
        //         }
        //     })
        // },
        // onOpenModal: ({ name }) => {
        //     dispatch({ type: SET_CURRENT_CONTRACT, contract })
        //     dispatch({ type: SET_MODAL, name })
        // },
        // onOpenCreateContract: ({ referenceId }) => {
        //     // dispatch({ type: SET_CURRENT_CONTRACT, referenceId })
        //     dispatch({ type: OPEN_CREATE_CONTRACT, referenceId })
        // },
    })

    return (
        <DropdownMenu right>
            {
                availableActions.map((action, i) => {
                    const { faIcon, label, onClick } = action;
                    return (
                        <DropdownItem key={`${action.name}-${i}`} href="#" onClick={onClick} className="contract-dropdown-item">
                            <i className={clsx(!faIcon.includes('fab') ? `fas fa-${faIcon}` : faIcon, 'mr-2')}></i>{label}  <br />
                        </DropdownItem>
                    )
                })
            }
        </DropdownMenu>
    )
}

function TemplateItem({ template, handleDoubleClick }) {

    const templates = useTemplatesState();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const dispatch = useTemplatesDispatch();

    const { updatedAt, createdAt, name, user, type, importedPdf, wordonline, _id, questions } = template;

    const { mutate: favoriteMutation } = useMutation((payload) => fetcher(templateAction(payload.templateId, 'favorite', {})), {
        onSuccess: (response) => {
            queryClient.invalidateQueries(['templates-all']);
            queryClient.invalidateQueries(['template-favourites']);
        }
    });
    const handleCheck = useCallback((e) => {
        e.stopPropagation();
        // e.preventDefault();
        dispatch({ type: SELECT, _id });
    }, [_id, dispatch]);

    const doubleClick = (e) => {
        if (restricted.includes(e.target.tagName)) return;
        handleDoubleClick(_id);
    }


    const handleSingleClick = useCallback((e) => {
        if (restricted.includes(e.target.tagName)) return;
        dispatch({ type: SET_CURRENT_TEMPLATE, template })

    }, [dispatch, template])

    const makeFavorite = useCallback(() => {
        favoriteMutation({ templateId: _id })
    }, [_id, favoriteMutation])

    return (
        <tr onDoubleClick={doubleClick} onClick={handleSingleClick} className="pointer">
            <td className="align-items-center text-center align-middle">
                <CustomInput id={template._id} type="checkbox" name="list-item" onChange={handleCheck} checked={templates.selected.includes(_id)} />
            </td>
            <td className="align-items-center align-middle text-left">
                {importedPdf ?
                    <img width="30px" src="/images/listing/pdf_document.png" alt="" /> :
                    wordonline ? <img width="30px" src="/images/listing/word_document.png" alt="" /> :
                        <img width="30px" src="/images/listing/document.png" alt="" />
                }
            </td>
            <td className="text-left text-nowrap align-middle">
                {truncate(name, 55)}
                <Button size="sm" color="default" onClick={makeFavorite}>
                    <i className="far fa-star"></i>
                </Button>
                <br></br>
                <small className="text-muted"> {type?.name || t('types.none')}</small>

                {/* <div id="description" name="description" className="template-type-name">
                     <p><span>{user.givenName} {user.familyName}</span></p> 
                </div> */}
            </td>
            <td className="text-center align-middle text-nowrap ">
                {parseDate(createdAt, 'PP')}
            </td>
            <td className="text-center align-middle text-nowrap ">
                {parseDate(updatedAt, 'PP')}
            </td>
            <td className="text-center align-middle text-nowrap ">
                {t('templates.status.private')}
            </td>
            <td className="text-center align-middle text-nowrap ">
                {questions?.length ?
                    t('templates.listing.questionnaire') :
                    t('templates.listing.template')
                }
            </td>
            <td className="text-center align-middle text-nowrap ">
                {user?.givenName} {user?.familyName}
            </td>
            <td className="align-items-center text-center align-middle text-nowrap ">
                <UncontrolledDropdown onClick={(e) => e.stopPropagation()}>
                    <DropdownToggle color="light">
                        <i className="fas fa-bars"></i>
                    </DropdownToggle>
                    <MenuDropdown template={template} />
                </UncontrolledDropdown>
            </td>
        </tr>
    )
}

function TableBody({ isLoading, templates = [], handleDoubleClick }) {

    const { t } = useTranslation();

    if (isLoading) {
        return <tr><td colSpan="9"><Spinner size="sm"></Spinner>{' '}{t('templates.listing.loading')}</td></tr>
    }
    if (!templates.length) {
        return <tr><td colSpan="9">{t('templates.favourites.empty')}</td></tr>
    }
    return (
        templates.map(template => <TemplateItem template={template} key={template._id} handleDoubleClick={handleDoubleClick} />)
    )
}

function MainTable({ handleDoubleClick }) {
    const { t } = useTranslation();
    const [templates, dispatch] = useTemplates();
    const { data: latestData = {}, isLoading } = useQuery(['templates-all', templates.meta], () => fetcher(fetchTemplates({ page: templates.meta.page, search: templates.meta.search })), { keepPreviousData: true });

    const handlePreviousPage = useCallback(() => dispatch({ type: PREVIOUS_PAGE }), [dispatch]);
    const handleNextPage = useCallback(() => dispatch({ type: NEXT_PAGE }), [dispatch]);
    const handleSelectAll = useCallback(() => dispatch({ type: SELECT_ALL_CONTRACTS, templates: latestData.docs }), [dispatch, latestData])
    const handlePageClick = useCallback((page) => dispatch({ type: SET_PAGE, page }), [dispatch]);

    return (<>
        <Breadcrumb>
            <CustomInput type="checkbox" onChange={handleSelectAll} checked={templates.allChecked} id="check-all"></CustomInput>
            <BreadcrumbItem>
                {t('templates.listing.title')}
            </BreadcrumbItem>
        </Breadcrumb>
       <Card className="border bg-white card-shadow rounded-lg" style={{ minHeight: '250px', height: 'calc(100% - 60px)'}}>
            <CardBody className="mt-2 h-100 overflow-y-hidden">
                <Table size="sm" className="bg-white text-center h-100 table-hover mb-0" style={{ maxHeight: '50px' }}>
                    <thead>
                        <tr className="text-center">
                            <th className="text-center"></th>
                            <th className="text-center"></th>
                            <th className="text-center">{t('templates.listing.name')}</th>
                            <th className="text-center">{t('templates.listing.created')}</th>
                            <th className="text-center">{t('templates.listing.updated')}</th>
                            <th className="text-center">{t('templates.listing.type')}</th>
                            <th className="text-center">{t('templates.listing.description')}</th>
                            <th className="text-center">{t('templates.listing.owner')}</th>
                            <th className="text-center"></th>
                        </tr>
                    </thead>
                    <tbody className="text-center">
                        <TableBody templates={latestData?.docs} isLoading={isLoading} handleDoubleClick={handleDoubleClick} />
                    </tbody>
                </Table>
            </CardBody>
            <CardFooter className="text-center" style={{ borderBottomLeftRadius: '22px', borderBottomRightRadius: '22px' }}>
                <Pagination
                    //{...templates.meta}
                    page={latestData.page || 1}
                    total={latestData.pages || 1}
                    isDisabled={isLoading}
                    onNextPage={handleNextPage}
                    onPrevPage={handlePreviousPage}
                    onPageClick={handlePageClick}
                />
            </CardFooter>
        </Card>
    </>
    )
}

function CreateTemplateModal() {

    const { t } = useTranslation();
    const history = useHistory();
    const templates = useTemplatesState();
    const ability = useProfileAbility();
    const dispatch = useTemplatesDispatch();
    const closeCreate = useCallback(() => dispatch({ type: TOGGLE_CREATE }), [dispatch])
    const { mutate: createTemplateMutation } = useMutation((payload) => fetcher(createTemplate(payload)), {
        onSuccess: (response) => {
            closeCreate();
            history.push(`/templates/editor/${response._id}`)
        }
    })

    const onDrop = async (files, source) => {
        const result = await fetcher(uploadFileV2(files[0]))
        createTemplateMutation({ ...result, name: result.BaseFileName, filename: result.BaseFileName, source })
    }
    const handleCreateBlank = () => {
        createTemplateMutation({ name: t('templates.new.new_template'), prefilled: true })
    }

    return (
        <Modal isOpen={templates.createIsOpen} centered size="lg">
            <ModalHeader toggle={closeCreate}>
                {t('templates.new.new_template')}
            </ModalHeader>
            <ModalBody className="bg-light text-center border-bottom">
                <small>{t('templates.new.select_type')}</small>
            </ModalBody>
            <ModalBody>
                <CardDeck className="text-center">
                    <Card className="border-0" hidden={ability.cannot('CREATE_PDF_TEMPLATE')}>
                        <Dropzone accept='application/pdf' onDrop={(files) => onDrop(files, 'pdf')} multiple={false}>
                            {({ getRootProps, getInputProps }) => (
                                <div {...getRootProps()} className="pointer" >
                                    <input {...getInputProps()} />
                                    <img src="/images/pdf.png" alt="" height="101" width="89" className="mb-1" />
                                    <p className="popoverTitle">PDF</p>
                                </div>
                            )}
                        </Dropzone>
                    </Card>
                    <Card className="border-bottom-0 border-top-0" hidden={ability.cannot('CREATE_WORD_TEMPLATE')}>
                        <Dropzone accept='.doc,.docx' onDrop={(files) => onDrop(files, 'wordonline')} multiple={false}>
                            {({ getRootProps, getInputProps }) => (
                                <div {...getRootProps()} className="pointer" action="word-template">
                                    <input {...getInputProps()} />
                                    <img src="/images/word.png" alt="" height="101" width="89" className="mb-1" />
                                    <p className="popoverTitle">Microsoft Word</p>
                                </div>
                            )}
                        </Dropzone>
                    </Card>
                    <Card className="border-0" onClick={handleCreateBlank}>
                        <div className="pointer">
                            <img src="/images/en-blanco.png" alt="" height="101" width="89" className="mb-1" />
                            <p className="popoverTitle">{t('templates.new.blank_title')}</p>
                        </div>
                    </Card>
                </CardDeck>
            </ModalBody>
        </Modal>
    )
}

function TemplatesContainer() {
    const api = useCancellablePromises();
    const history = useHistory();
    const { t } = useTranslation();
    const profileAbility = useProfileAbility();
    const templates = useTemplatesState();
    const dispatch = useTemplatesDispatch();

    const { mutate: createContractFromTemp } = useMutation((payload) => fetcher(createContractFromTemplate(payload)), {
        onSuccess: (response) => {
            history.push(`/contracts/editor/${response._id}`)
        }
    });

    const handleDoubleClick = async (_id) => {
        api.clearPendingPromises();
        if (!profileAbility.can('DOUBLECLICK_TEMPLATE_TO_CONTRACT')) return;
        if (await bootbox.confirm(t('templates.new.create'))) {
            createContractFromTemp({ _id })
        }
    };

    const toggleSetDetail = useCallback(() => {
        dispatch({ type: TOGGLE_TEMPLATE_DETAIL })
    }, [dispatch])

    return (
        <div className="h-100 d-flex flex-column">
            <CreateTemplateModal />
            <TemplateDetail isOpen={templates.detailOpen} toggle={toggleSetDetail} initialContract={templates.currentContract} />
            <Header />

            <Col className="mt-3">
                <div className="d-flex">
                    <Favourites handleDoubleClick={handleDoubleClick} />
                </div>
            </Col>

            <div className="flex-grow-1 mb-3">
                <MainTable handleDoubleClick={handleDoubleClick} />
            </div>
        </div>
    )
}

function Templates() {

    return (
        <TemplatesProvider>
            <TemplatesContainer />
        </TemplatesProvider>
    )
}

export default Templates


